import { SsoButton } from "@/components/settings/sso/SsoButton";
import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { Spinner } from "@/components/loaders/spinner";
import { cx } from "@/utils/classnames";
import { amplitudeTrack } from "@/utils/hooks/use-amplitude-pageview";
import { SignInOptions, signIn } from "next-auth/react";
import { useRouter } from "next/router";
import { useState } from "react";

export interface LoginProviderButtonProps {
  id: string;
  name: string;
  type: string;
  entityId?: string;
  signInOptions?: SignInOptions;
  purpose: string;
  /**
   * Runs — and must resolve — before the browser leaves for the identity provider. The
   * invite flow uses it to park its signup code in a cookie; a rejection aborts the
   * sign-in, because arriving at the provider without that cookie lands the user on the
   * no-organization page with nothing to claim.
   */
  onBeforeSignIn?: () => Promise<void>;
  /** Receives pre-sign-in failures for flows that display their own error. */
  onProviderSignInError?: (error: unknown) => void;
  /** "ring": the sign-up design's v2 secondary button with a monochrome mark and "Continue with …" copy. */
  appearance?: "default" | "ring";
}

// Sign-up (Ring) copy and marks; providers without an entry show their own name and no mark.
const RING_PROVIDERS: Record<string, { label: string; icon: string; size: number }> = {
  google: { label: "Continue with Google", icon: "/signup/icon-google.svg", size: 16 },
  "azure-ad": { label: "Azure Active Directory", icon: "/signup/icon-azure.svg", size: 18 },
};

export const LoginProviderButton = ({
  id,
  name,
  type,
  entityId,
  signInOptions,
  purpose,
  onBeforeSignIn,
  onProviderSignInError,
  appearance = "default",
}: LoginProviderButtonProps) => {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { NEXT_PUBLIC_AMPLITUDE_API_KEY } = useNextEnvVars();

  const startSignIn = async () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => setLoading(false), 10_000);
    amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, "click login option", {
      provider: id,
      purpose: purpose,
    });
    if (onBeforeSignIn) {
      try {
        await onBeforeSignIn();
      } catch (err) {
        console.error("[Chalk] Aborting sign-in: pre-sign-in step failed", err);
        onProviderSignInError?.(err);
        setLoading(false);
        return;
      }
    }
    if (type === "saml") {
      await router.push({
        pathname: "/api/auth/login/saml",
        query: {
          team: id,
          entityId,
          callbackUrl: signInOptions?.callbackUrl,
        },
      });
    } else {
      await signIn(id, signInOptions);
    }
  };

  if (appearance === "ring") {
    const ring = RING_PROVIDERS[id];
    return (
      <button
        type="button"
        data-testid={`login-provider-${id}`}
        onClick={() => void startSignIn()}
        aria-busy={loading || undefined}
        className="relative inline-flex h-9 w-full items-center justify-center gap-2 rounded-[6px] border border-[#E4E4E7] bg-white px-3 text-[12px] font-medium leading-4 tracking-[0.16px] text-[#27272A] shadow-[0_1px_2px_0_rgb(0_0_0_/_0.03)] transition-colors duration-[120ms] hover:bg-[#FAFAFA] active:bg-[#F4F4F5] focus-visible:outline-none focus-visible:shadow-[0_0_0_1px_rgb(59_176_137_/_0.5),0_1px_2px_0_rgb(0_0_0_/_0.03)]"
      >
        {ring != null ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={ring.icon} width={ring.size} height={ring.size} alt="" />
        ) : null}
        {ring?.label ?? name}
        {loading ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner />
          </span>
        ) : null}
      </button>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <a
      data-testid={`login-provider-${id}`}
      className={cx(
        "rounded-md ease-in-out duration-200 cursor-pointer relative",
        loading ? "hover:bg-current" : "",
      )}
      onClick={() => void startSignIn()}
      key={"login-" + id}
    >
      <SsoButton id={id} name={name} type={type} />
      {loading && (
        <div className={"absolute top-[8px] right-[8px]"}>
          <Spinner />
        </div>
      )}
    </a>
  );
};
